interface ApiErrorResponse {
  data?: {
    error?: string | string[];
    message?: string | string[];
    details?: string | string[];
    [key: string]: string | string[] | undefined;
  };
  status?: number;
  statusText?: string;
}

interface NetworkError {
  message?: string;
  code?: string;
  request?: any;
}

const FIELD_TRANSLATIONS: Record<string, string> = {
  cnpj: 'CNPJ',
  corporate_name: 'Razão Social',
  name: 'Nome Fantasia',
  email: 'E-mail',
  password: 'Senha',
  full_name: 'Nome Completo',
  cellphone: 'Telefone',
  category: 'Categoria',
  description: 'Descrição',
  site: 'Site',
  cover_photo: 'Foto de Capa',
  logo: 'Logo',
  postal_code: 'CEP',
  street: 'Rua',
  number: 'Número',
  complement: 'Complemento',
  neighborhood: 'Bairro',
  state: 'Estado',
  city: 'Cidade',
  country: 'País',
  discount: 'Desconto',
  title_discount: 'Título do Desconto',
  name_discount: 'Nome do Desconto',
  link: 'Link',
  coupon: 'Cupom',
  image: 'Imagem',
  rules: 'Regras',
  mode: 'Modo',
  type: 'Tipo',
};

const ERROR_MESSAGES: Record<string, string> = {
  'Network Error': 'Erro de conexão. Verifique sua internet e tente novamente.',
  'timeout of': 'A requisição demorou muito para responder. Tente novamente.',
  'Request failed with status code 400': 'Dados inválidos. Verifique os campos preenchidos.',
  'Request failed with status code 401': 'Não autorizado. Faça login novamente.',
  'Request failed with status code 403': 'Acesso negado. Você não tem permissão para esta ação.',
  'Request failed with status code 404': 'Recurso não encontrado.',
  'Request failed with status code 500': 'Erro interno do servidor. Tente novamente mais tarde.',
  'Request failed with status code 502': 'Servidor temporariamente indisponível. Tente novamente.',
  'Request failed with status code 503': 'Serviço temporariamente indisponível. Tente novamente.',
  'ECONNABORTED': 'A requisição demorou muito. Tente novamente.',
  'ENOTFOUND': 'Não foi possível conectar ao servidor. Verifique sua conexão.',
  'ECONNREFUSED': 'Conexão recusada. O servidor pode estar indisponível.',
};

function translateField(field: string): string {
  return FIELD_TRANSLATIONS[field] || field;
}

function formatValidationErrors(errorData: Record<string, string | string[]>): string {
  const errors: string[] = [];

  Object.entries(errorData).forEach(([field, messages]) => {
    const fieldName = translateField(field);
    const messageArray = Array.isArray(messages) ? messages : [messages];
    
    messageArray.forEach((msg) => {
      if (typeof msg === 'string' && msg.trim()) {
        if (msg.includes('obrigatório') || msg.includes('inválido') || msg.includes('já existe')) {
          errors.push(`${fieldName}: ${msg}`);
        } else {
          errors.push(`${fieldName}: ${translateErrorMessage(msg)}`);
        }
      }
    });
  });

  return errors.length > 0 ? errors.join('\n') : 'Erro de validação. Verifique os campos preenchidos.';
}

function translateErrorMessage(message: string): string {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('required') || lowerMessage.includes('obrigatório')) {
    return 'Este campo é obrigatório';
  }
  if (lowerMessage.includes('invalid') || lowerMessage.includes('inválido')) {
    return 'Valor inválido';
  }
  if (lowerMessage.includes('already exists') || lowerMessage.includes('já existe')) {
    return 'Já existe um registro com este valor';
  }
  if (lowerMessage.includes('not found') || lowerMessage.includes('não encontrado')) {
    return 'Não encontrado';
  }
  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('não autorizado')) {
    return 'Não autorizado. Faça login novamente.';
  }
  if (lowerMessage.includes('forbidden') || lowerMessage.includes('proibido')) {
    return 'Acesso negado';
  }
  if (lowerMessage.includes('too large') || lowerMessage.includes('muito grande')) {
    return 'Arquivo muito grande';
  }
  if (lowerMessage.includes('file type') || lowerMessage.includes('tipo de arquivo')) {
    return 'Tipo de arquivo não permitido';
  }

  return message;
}

function isNetworkError(error: any): boolean {
  if (!error) return false;
  if (error.request && !error.response) return true;
  if (error.code === 'ECONNABORTED' || error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
    return true;
  }
  const message = error.message || '';
  if (message.includes('Network Error') || message.includes('timeout') || message.includes('ECONN')) {
    return true;
  }
  return false;
}

function formatNetworkError(error: NetworkError): string {
  const message = error.message || '';
  const code = error.code || '';

  for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
    if (message.includes(key) || code.includes(key)) {
      return value;
    }
  }

  if (code === 'ECONNABORTED' || message.includes('timeout')) {
    return 'A requisição demorou muito para responder. Verifique sua conexão e tente novamente.';
  }

  if (code === 'ENOTFOUND' || code === 'ECONNREFUSED') {
    return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
  }

  return 'Erro de conexão. Verifique sua internet e tente novamente.';
}

function formatHttpError(error: any): string {
  const response = error.response as ApiErrorResponse | undefined;
  const status = response?.status || error.status;
  const data = response?.data;

  if (status === 400) {
    if (data) {
      if (data.details) {
        const details = Array.isArray(data.details) ? data.details : [data.details];
        return details.join('\n');
      }
      if (data.error) {
        const errorMsg = Array.isArray(data.error) ? data.error.join('\n') : data.error;
        return translateErrorMessage(errorMsg);
      }
      if (data.message) {
        const msg = Array.isArray(data.message) ? data.message.join('\n') : data.message;
        return translateErrorMessage(msg);
      }
      const validationErrors = formatValidationErrors(data as Record<string, string | string[]>);
      if (validationErrors) {
        return validationErrors;
      }
    }
    return 'Dados inválidos. Verifique os campos preenchidos.';
  }

  if (status === 401) {
    return 'Sessão expirada. Faça login novamente.';
  }

  if (status === 403) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (status === 404) {
    return 'Recurso não encontrado.';
  }

  if (status >= 500) {
    return 'Erro interno do servidor. Tente novamente mais tarde ou entre em contato com o suporte.';
  }

  if (data?.message) {
    const msg = Array.isArray(data.message) ? data.message.join('\n') : data.message;
    return translateErrorMessage(msg);
  }

  if (data?.error) {
    const errorMsg = Array.isArray(data.error) ? data.error.join('\n') : data.error;
    return translateErrorMessage(errorMsg);
  }

  return `Erro ao processar requisição (${status || 'desconhecido'}). Tente novamente.`;
}

export function formatPartnerApiError(error: any): string {
  if (!error) {
    return 'Erro desconhecido. Tente novamente.';
  }

  if (isNetworkError(error)) {
    return formatNetworkError(error);
  }

  if (error.response) {
    return formatHttpError(error);
  }

  if (error.message) {
    const message = error.message;
    for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
      if (message.includes(key)) {
        return value;
      }
    }
    return translateErrorMessage(message);
  }

  return 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.';
}
